/** MPC-00001 — Master Platform Constitution framework (mirrors pkg/masterplatform) */

export const MPC_ID = 'MPC-00001';

export interface ComplianceKPI {
  label: string;
  value: string;
  status: string;
  compliant: boolean;
}

export interface ArchitectureKernelRow {
  category: string;
  name: string;
  status: string;
}

export interface TenantEventRow {
  area: string;
  rule: string;
  status: string;
}

export interface AIGovernanceRow {
  layer: string;
  detail: string;
  status: string;
}

export interface SecurityExperienceRow {
  domain: string;
  standard: string;
  status: string;
}

export interface QualityObservabilityRow {
  category: string;
  item: string;
  status: string;
  score: number;
}

export interface PlatformStandardsRow {
  domain: string;
  principle: string;
  status: string;
}

export interface GovernanceComplianceRow {
  rule: string;
  status: string;
  severity: string;
}

export const CORE_PRINCIPLES = [
  'AI First', 'Business First', 'Marketplace Agnostic', 'Enterprise Grade', 'Explainable',
  'Secure by Design', 'Accessible by Design', 'Composable', 'Event Driven', 'API First',
  'Automation First', 'Cloud Ready', 'Extensible', 'Observable', 'Highly Available', 'Fault Tolerant', 'Future Ready',
];

export const ARCHITECTURAL_PRINCIPLES = [
  'Domain Driven Design', 'Event Driven Architecture', 'Service Oriented Architecture', 'Canonical Commerce Model',
  'CQRS', 'Saga Pattern', 'Outbox Pattern', 'Repository Pattern', 'Dependency Injection', 'Clean Architecture',
  'Hexagonal Architecture', 'Stateless Services', 'Immutable Events',
];

export const CANONICAL_ENTITIES = [
  'Organization', 'Workspace', 'Brand', 'Marketplace', 'Product', 'SKU', 'Variant', 'Inventory',
  'Warehouse', 'Supplier', 'Purchase Order', 'Campaign', 'Advertisement', 'Customer', 'Order',
  'Shipment', 'Return', 'Refund', 'Settlement', 'Invoice', 'Payment', 'Ledger', 'Workflow',
  'Task', 'Notification', 'Knowledge', 'DNA', 'AI Agent', 'Prompt', 'Feature Flag', 'Release', 'Audit Event',
];

export const TENANT_SCOPES = ['Organization', 'Workspace', 'Department', 'Brand', 'Marketplace', 'User', 'Role', 'Permissions'];

export const EVENT_FIELDS = [
  'Event ID', 'Correlation ID', 'Causation ID', 'Timestamp', 'Version', 'Tenant ID',
  'Organization ID', 'Producer', 'Payload', 'Security Classification', 'Idempotency Key',
];

export const EVENT_CATEGORIES = ['Business', 'AI', 'Marketplace', 'System', 'Security', 'Governance', 'Operational'];

export const AI_REQUIREMENTS = [
  'Explainable', 'Auditable', 'Version Controlled', 'Prompt Versioned', 'Model Versioned',
  'Human Override Capable', 'Confidence Scored', 'Policy Governed', 'Brand Governed', 'Marketplace Governed',
];

export const DNA_LAYERS = [
  'Brand DNA', 'Product DNA', 'Customer Intent DNA', 'Growth DNA', 'Supply Chain DNA', 'CX DNA',
  'Financial DNA', 'Decision DNA', 'Organizational DNA', 'Knowledge DNA', 'Experience DNA',
];

export const PROMPT_GOVERNANCE_LAYERS = [
  'Global Rules', 'Organization Rules', 'Brand Rules', 'Marketplace Rules', 'Product Rules',
  'Workflow Rules', 'Task Context', 'DNA Context', 'User Context', 'Prompt',
];

export const SECURITY_PRINCIPLES = [
  'Zero Trust', 'Least Privilege', 'MFA', 'RBAC', 'Audit Everything', 'Encryption',
  'Secret Rotation', 'API Signing', 'Replay Protection', 'Rate Limiting', 'Secure Defaults', 'OWASP Compliance',
];

export const ACCESSIBILITY_STANDARDS = [
  'WCAG 2.2 AA', 'Keyboard Navigation', 'Screen Reader', 'Reduced Motion', 'High Contrast', 'Responsive Design', 'Localization',
];

export const DESIGN_PRINCIPLES = [
  'Enterprise Design', 'Modern UX', 'Minimal Cognitive Load', 'Role Based Navigation', 'Consistency',
  'Predictability', 'Fast Interaction', 'Explainable AI', 'Design Tokens', 'Reusable Components',
];

export const OBSERVABILITY_SIGNALS = [
  'Logs', 'Metrics', 'Traces', 'Business Events', 'Marketplace Events', 'AI Events', 'Security Events', 'Infrastructure Events',
];

export const QUALITY_VALIDATIONS = [
  'Static Analysis', 'Unit Tests', 'Integration Tests', 'E2E Tests', 'AI Validation',
  'Marketplace Validation', 'Accessibility Validation', 'Performance Validation', 'Security Validation', 'Release Certification',
];

export const RELEASE_GATES = [
  'Quality Gate', 'Security Gate', 'Performance Gate', 'Marketplace Gate',
  'AI Gate', 'Accessibility Gate', 'Business Gate', 'Executive Approval',
];

export const DATABASE_PRINCIPLES = [
  'Versioning', 'Soft Delete', 'Audit', 'Created By', 'Updated By', 'Tenant Ownership', 'Optimistic Locking', 'History',
];

export const API_PRINCIPLES = [
  'RESTful', 'Versioned', 'Documented', 'Secure', 'Observable', 'Rate Limited', 'Idempotent', 'Marketplace Independent',
];

export const TESTING_TYPES = [
  'Unit', 'Integration', 'Contract', 'E2E', 'Marketplace', 'AI', 'Accessibility', 'Load', 'Stress', 'Chaos', 'Recovery',
];

export const DEPLOYMENT_PRINCIPLES = [
  'Blue Green', 'Canary', 'Feature Flags', 'Rollback', 'Zero Downtime', 'Infrastructure as Code', 'Continuous Delivery',
];

export const DEFINITION_OF_DONE = [
  'Business Logic Complete', 'UI Complete', 'API Complete', 'Database Complete', 'AI Complete',
  'Testing Complete', 'Accessibility Complete', 'Documentation Complete', 'Training Complete',
  'Observability Complete', 'Security Complete', 'Performance Validated', 'Approved',
];

export const NON_NEGOTIABLE_RULES = [
  'Tenant Isolation', 'Security', 'Auditability', 'Explainability', 'Marketplace Compliance',
  'Accessibility', 'Performance Standards', 'Business Integrity', 'Financial Accuracy', 'Customer Privacy',
];

export const ARCHITECTURE_STAGES = [
  'Constitution Overview', 'Architecture Kernel', 'Tenant & Event Model', 'AI Governance',
  'Security & Experience', 'Quality & Observability', 'Platform Standards', 'Governance Compliance',
];

export const LIVE_ROUTES: Record<string, string> = {
  governance: '/governance',
  enterpriseGA: '/platform/enterprise-ga',
  designSystem: '/platform/design-system',
  command: '/command',
};

export const DEMO_KPIS: ComplianceKPI[] = [
  { label: 'Core Principles', value: '17/17', status: 'enforced', compliant: true },
  { label: 'Architectural Principles', value: '13/13', status: 'enforced', compliant: true },
  { label: 'Canonical Entities', value: '32/32', status: 'registered', compliant: true },
  { label: 'Release Gates', value: '8/8', status: 'active', compliant: true },
  { label: 'Non-Negotiable Rules', value: '10/10', status: 'zero_tolerance', compliant: true },
  { label: 'Definition of Done', value: '13/13', status: 'mandatory', compliant: true },
];

export const DEMO_ARCHITECTURE: ArchitectureKernelRow[] = [
  { category: 'Architecture', name: 'Domain Driven Design', status: 'enforced' },
  { category: 'Architecture', name: 'Event Driven Architecture', status: 'enforced' },
  { category: 'Architecture', name: 'Clean Architecture', status: 'enforced' },
  { category: 'Canonical', name: 'Product', status: 'registered' },
  { category: 'Canonical', name: 'Order', status: 'registered' },
  { category: 'Canonical', name: 'Inventory', status: 'registered' },
  { category: 'Canonical', name: 'AI Agent', status: 'registered' },
  { category: 'Canonical', name: 'DNA', status: 'registered' },
];

export const DEMO_TENANT_EVENT: TenantEventRow[] = [
  { area: 'Multi-Tenant', rule: 'Absolute tenant isolation', status: 'enforced' },
  { area: 'Multi-Tenant', rule: 'Tenant ownership metadata on every object', status: 'enforced' },
  { area: 'Events', rule: 'Immutable event emission on every business action', status: 'enforced' },
  { area: 'Events', rule: 'Correlation ID + Causation ID required', status: 'enforced' },
  { area: 'Events', rule: 'Idempotency key on all events', status: 'enforced' },
  { area: 'Events', rule: 'Security classification on payload', status: 'enforced' },
];

export const DEMO_AI_GOVERNANCE: AIGovernanceRow[] = [
  { layer: 'AI Constitution', detail: 'Confidence + reasoning on every response', status: 'enforced' },
  { layer: 'Brand DNA', detail: 'v2.4 production', status: 'active' },
  { layer: 'Product DNA', detail: 'v1.8 production', status: 'active' },
  { layer: 'Growth DNA', detail: 'v1.2 production', status: 'active' },
  { layer: 'Prompt Governance', detail: '10-layer assembly hierarchy', status: 'enforced' },
  { layer: 'Prompt Version', detail: 'v3.2 — permanent history', status: 'archived' },
];

export const DEMO_SECURITY_EXPERIENCE: SecurityExperienceRow[] = [
  { domain: 'Security', standard: 'Zero Trust + RBAC', status: 'enforced' },
  { domain: 'Security', standard: 'OWASP Compliance', status: 'validated' },
  { domain: 'Accessibility', standard: 'WCAG 2.2 AA', status: 'compliant' },
  { domain: 'Accessibility', standard: 'Keyboard Navigation', status: 'compliant' },
  { domain: 'Design System', standard: 'Design Tokens + Reusable Components', status: 'active' },
  { domain: 'Design System', standard: 'Explainable AI UI patterns', status: 'active' },
];

export const DEMO_QUALITY: QualityObservabilityRow[] = [
  { category: 'Quality', item: 'Unit Tests', status: 'passing', score: 100 },
  { category: 'Quality', item: 'AI Validation', status: 'passing', score: 98 },
  { category: 'Quality', item: 'Accessibility Validation', status: 'passing', score: 96 },
  { category: 'Observability', item: 'Logs + Metrics + Traces', status: 'active', score: 100 },
  { category: 'Release', item: 'Quality Gate', status: 'passed', score: 100 },
  { category: 'Release', item: 'Security Gate', status: 'passed', score: 100 },
  { category: 'Release', item: 'Executive Approval', status: 'approved', score: 100 },
];

export const DEMO_STANDARDS: PlatformStandardsRow[] = [
  { domain: 'Database', principle: 'Tenant ownership + audit + soft delete', status: 'enforced' },
  { domain: 'API', principle: 'Versioned + marketplace independent', status: 'enforced' },
  { domain: 'Automation', principle: 'Policy-governed AI agents', status: 'active' },
  { domain: 'Knowledge', principle: 'Searchable + AI consumable', status: 'active' },
  { domain: 'Testing', principle: 'Unit + integration + chaos + recovery', status: 'mandatory' },
  { domain: 'Deployment', principle: 'Blue-green + canary + zero downtime', status: 'active' },
  { domain: 'Business Continuity', principle: 'DR drills + runbooks', status: 'scheduled' },
];

export const DEMO_GOVERNANCE: GovernanceComplianceRow[] = [
  { rule: 'Tenant Isolation', status: 'compliant', severity: 'critical' },
  { rule: 'Security', status: 'compliant', severity: 'critical' },
  { rule: 'Auditability', status: 'compliant', severity: 'critical' },
  { rule: 'Explainability', status: 'compliant', severity: 'critical' },
  { rule: 'Marketplace Compliance', status: 'compliant', severity: 'critical' },
  { rule: 'Accessibility', status: 'compliant', severity: 'high' },
  { rule: 'Financial Accuracy', status: 'compliant', severity: 'critical' },
  { rule: 'Customer Privacy', status: 'compliant', severity: 'critical' },
];

export function buildConstitutionDashboard() {
  return {
    core_principles: CORE_PRINCIPLES.length,
    architectural_principles: ARCHITECTURAL_PRINCIPLES.length,
    canonical_entities: CANONICAL_ENTITIES.length,
    release_gates: RELEASE_GATES.length,
    non_negotiable_rules: NON_NEGOTIABLE_RULES.length,
  };
}
